// Re-export VectorIcon from components
export type { VectorIcon } from '@microservices/wiskey-react-components'
import type { VectorIcon as VectorIconType } from '@microservices/wiskey-react-components'

export * from './incidentReport'

// Re-export GETCommand and ACTION_CODE from @types
export type { CommandParameterDTO, GETCommand } from '@types'
import type { GETCommand as GETCommandType } from '@types'
export type { ACTION_CODE } from '@constants'

export interface AppWsNotification {
  internalId: string
  objectCode: string
  eventName: string
  filter?: string
  expands?: string
  titleJs: string
  messageJs?: string
  channelId?: string
  playSound?: boolean
}

export interface MobileApp {
  id: number
  internalId: string
  title: string
  startScreen: string
  icon: VectorIconType | null
  backgroundColor: string
  /** Prevent device screen from sleeping while this app is active */
  keepAwake?: boolean
  drawerItems: DrawerItem[]
  screenIds: number[]
  eventFlows?: MobileEventFlow[]
  wsNotifications?: AppWsNotification[]
}

export interface DrawerItem {
  internalId: string
  label: string
  icon?: VectorIconType | null
  isJS?: boolean
}

export interface MobileScreen {
  id: number
  internalId: string
  title: string
  type: ScreenType
  /** Background color for entire screen (e.g. #FFFFFF) */
  backgroundColor?: string
  listItemConfigId?: string
  formConfigId?: string
  searchAssistantId?: string // ID конфигурации Search Assistant
  backgroundType?: 'LIST' | 'FORM' | 'MAP'
  /** SPLIT: конфиг списка для фона */
  backgroundListItemConfigId?: string
  /** SPLIT: конфиг формы для фона */
  backgroundFormConfigId?: string
  /** SPLIT: конфиг формы внутри bottom sheet */
  sheetFormConfigId?: string
  /** SPLIT: snap points для bottom sheet, например ['25%', '50%', '90%'] */
  snapPoints?: string[]
  /** SPLIT: начальный индекс snap point (0-based) */
  initialSnapIndex?: number
  /** SPLIT: eventFlows уровня Split (ON_SHEET_OPENED, ON_SHEET_CLOSED и др.) */
  eventFlows?: MobileEventFlow[]
  /** MAP type: ссылка на конфиг из Mobile Maps или inline-поля (если mapConfigId не задан) */
  mapConfigId?: string
  /** MAP type (inline, если mapConfigId не задан) */
  mapCenter?: [number, number]
  mapZoom?: number
  mapStyleUrl?: string
  showCurrentLocation?: boolean
  mapPointGroups?: MobileMapPointGroup[]
  mapRouteTargetPoint?: MobileMapRouteTarget
  mapVariables?: Variable[]
  mapEventFlows?: MobileEventFlow[]
  /** SPLIT backgroundType === 'MAP': ссылка на конфиг или inline */
  backgroundMapConfigId?: string
  /** SPLIT backgroundType === 'MAP' (inline) */
  backgroundMapCenter?: [number, number]
  backgroundMapZoom?: number
  backgroundShowCurrentLocation?: boolean
  backgroundMapPointGroups?: MobileMapPointGroup[]
  backgroundMapRouteTargetPoint?: MobileMapRouteTarget
}

/** Группа точек на карте; данные из переменной (массив объектов с полями latitude, longitude). */
export interface MobileMapPointGroup {
  internalId: string
  title: string
  variableName: string
  color: string
  icon?: string
  titleField?: string
}

/** Целевая точка маршрута — переменная с объектом, имеющим latitude и longitude. */
export interface MobileMapRouteTarget {
  variableName: string
}

/** Конфигурация карты (Mobile Maps). Используется в экранах типа MAP и в SPLIT с фоном Map. */
export interface MobileMap {
  id: string
  internalId: string
  name: string
  mapCenter: [number, number]
  mapZoom: number
  mapStyleUrl?: string
  showCurrentLocation?: boolean
  mapPointGroups?: MobileMapPointGroup[]
  mapRouteTargetPoint?: MobileMapRouteTarget
  variables?: Variable[]
  eventFlows?: MobileEventFlow[]
}

export type ScreenType = 'LIST' | 'FORM' | 'SPLIT' | 'MAP'

export interface DataSource {
  bindType: 'command' | 'js' | 'prefilled'
  commandKey?: string
  jsCode?: string
  prefilledVariable?: string // internalId переменной из prefilledVariables
}

export interface PrefilledVariable {
  internalId: string // Ссылка на internalId переменной из MobileListItem.variables
  required?: boolean
}

/** FAB icon config (value, size, type, color) */
export interface FabIconConfig {
  value: string
  size?: number
  type?: string // MaterialDesignIcons | MaterialIcons | FontAwesome5
  color?: string
}

/** FAB (Floating Action Button) config for list screens */
export interface FabConfig {
  visibleIfJs?: string
  icon: FabIconConfig
  backgroundColor: string
}

export interface MobileListItem {
  id: number
  internalId: string
  title: string
  itemType: string
  getDataCommands?: GETCommandType[] | null
  dataSource?: DataSource
  prefilledVariables?: PrefilledVariable[] // Переменные, которые можно предзаполнить извне
  icon?: IconConfig
  rows: Row[]
  onItemPress?: string
  configMode?: 'simple' | 'advanced'
  itemTemplate?: ItemTemplate
  variables?: Variable[]
  eventFlows?: MobileEventFlow[]
  /** FAB config for list (optional). EventFlow with eventCode ON_FAB_PRESS handles press. */
  fab?: FabConfig
  /** Enable search bar above the list (requires getDataCommands) */
  enableSearch?: boolean
  /** Fields used for RSQL search filter (e.g. ["model", "reg_number"]) */
  searchFields?: string[]
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
}

export interface IconConfig {
  size?: number
  type?: string
  value: string
  isJS?: boolean
  displayRule?: DisplayRule
  /** Icon color (e.g. "#FF9800"). Has priority over displayRule/stylesheet. */
  color?: string
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
}

export interface DisplayRule {
  id: number
  code: string
  internalId?: string
  title: string
  objectCode: string
  objectTitle: string
  objectField?: string
  isJS: boolean
  jsSetting?: JsSetting
  displayRuleValues: any[] | null
}

export interface JsSetting {
  styleSettingScript: string
  styleSettings: StyleSetting[]
}

export interface StyleSetting {
  code: string
  title: string
  background: string
  padding?: number
  settingText: SettingText
  settingBorder: SettingBorder
}

export interface SettingText {
  fontFamily: string
  fontColor: string
  alignment: string
  fontSize: number
  isBold: boolean
  isItalic: boolean
  padding?: number
  bgFontColor?: string
}

export interface SettingBorder {
  type: string
  color: string
}

export interface Row {
  id?: number
  elements: RowElement[]
}

export interface RowElement {
  code: string
  order: number
  title: string
  value: string
  pinnedColumn: string | null
  objectValue: string | null
  pathArray: PathArrayItem[] | null
  actions: any[]
  columnToSort: string | null
  objectCode: string
  valueType: string | null
  fieldType: string | null
  bindType: 'field' | 'js'
  formatDate: string | null
  editable: boolean
  enumDescription: boolean
  visibility: boolean
  useLocalParameters: boolean
  useDisplayRule: boolean
  isPk: boolean | null
  parameters: FieldParameter[]
  restrictions: any
  valueFormat: any
  displayRule: DisplayRule | null
  preFill: any
  viewStyle?: any
  hideIfUndefined?: boolean
  hideWhenEmpty?: boolean
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
}

export interface PathArrayItem {
  objectCode: string
  field: string
  valueType: string
  isPk: boolean
  isInternalId: boolean
  model: any
}

export interface FieldParameter {
  paramKey: string
  param: Parameter
}

export interface Parameter {
  code: string
  title: string
  description: string
  propertiesType: string
  typeCode: string
  state: string
  properties: ParameterProperty[]
}

export interface ParameterProperty {
  key: string
  value: string
}

export interface MobileForm {
  id: number
  internalId: string
  title: string
  elements: FormElement[]
  bottomElements: FormElement[]
  variables?: Variable[]
  eventFlows?: MobileEventFlow[]
}

export interface Variable {
  id?: string | number
  internalId: string
  variableType: 'single' | 'multiple' // single value or array
  valueType: string // string, number, date, datetime, boolean, etc.
  objectCode?: string // Optional object reference
  defaultValue?: any
  /** Сохранять значение между сессиями приложения (mobile: MMKV) */
  persistent?: boolean
}

export interface FormElement {
  id?: string // Unique identifier for BUTTON and SWIPE elements
  internalId?: string // User-defined stable ID for references and event flows
  order?: number
  size?: number
  type: FormElementType
  title?: string
  titleJs?: string // JS expression for dynamic title (for BUTTON and SWIPE)
  textAlign?: 'LEFT' | 'CENTER' | 'RIGHT'
  labelPosition?: 'TOP' | 'LEFT' | 'NONE'
  editable?: boolean
  titleHidden?: boolean
  label?: string
  value?: string
  format?: 'datetime' | 'date' | 'time' | string
  data?: FormFieldData
  command?: GETCommandType
  formToLcConnections?: any[]
  formCode?: string
  actions?: SwipeAction[]
  chooseActionJs?: string
  /** SWIPE: background color (e.g. #e3f2fd) */
  backgroundColor?: string
  /** BUTTON: button background color (e.g. #3d8761) */
  buttonColor?: string
  visibleIfJs?: string
  // For EMBEDDED_LIST
  embeddedListItemId?: string // ID существующей MobileListItem конфигурации
  embeddedListConfig?: Partial<MobileListItem> // Или inline конфигурация (опционально)
  selectedIdsVariable?: string // Имя переменной для хранения выбранных ID
  showCheckboxes?: boolean // Показывать ли чекбоксы (по умолчанию true)
  itemIdField?: string // Поле для идентификации элемента (по умолчанию "_id")
  contextMenuActions?: ContextMenuAction[] // Встроенные действия контекстного меню
  // Header and summary for EMBEDDED_LIST
  listHeader?: string // Заголовок секции (например "Проверки")
  listSubheader?: string // Статический подзаголовок
  listSubheaderJs?: string // JS выражение для динамического подзаголовка (например "Всего: " + items.length)
  selectAllEnabled?: boolean // Показывать "Выбрать все"
  selectAllText?: string // Текст для "Выбрать все" (например "Проверены все")
  // Variable bindings for EMBEDDED_LIST (maps ListItem prefilledVariables to Form variables)
  variableBindings?: Record<string, string> // { prefilledVarName: formVariableName }
  /** When JS returns true, list is read-only (no checkboxes, no context menu) */
  readOnlyIfJs?: string
  // For LIST_ITEM (single item display)
  listItemConfigId?: string // ID существующей MobileListItem конфигурации для отображения одного элемента
  listItemDataVariable?: string // Имя переменной, содержащей данные для отображения
  // For ACCORDION
  accordionChildren?: FormElement[]
  style?: {
    backgroundColor?: string
    borderRadius?: number
    padding?: number
    marginBottom?: number
  }
  // For FILE
  fileTypes?: string[] // e.g. ['image']
  fileVariableName?: string // internalId of the Object variable holding the file data
}

export type FormElementType =
  | 'FIELD'
  | 'PHONE_NUMBER'
  | 'DIVIDER'
  | 'TITLE'
  | 'BUTTON'
  | 'SWIPE'
  | 'EMBEDDED_LIST'
  | 'LIST_ITEM'
  | 'ACCORDION'
  | 'FILE'

export interface FormFieldData {
  code: string
  title: string
  value: string
  /** For bindType 'js': path to raw value (value is then JS code returning display string) */
  valuePath?: string
  objectCode: string
  variableName?: string // Reference to variable
  variableObjectCode?: string // Object code from variable (if object type)
  valueType: string
  bindType: string
  embeddedObjectCode: string | null
  objectValue: string | null
  formCode: string
  pathArray: any[] | null
  formatDate: string | null
  valueFormat: any
  objectFormCode: string | null
  objectFormTitle: string | null
  params: any
  type: string
  userRequired: boolean
  defaultRequired: boolean
  editField: boolean
  isMultiline: boolean
  isAddFieldValues: boolean
  isEnabledTimeMenu: boolean
  fieldValues: any[]
  dropDownList: any
  dropDownWindow: any
  isDefaultPlaceholder: boolean
  placeholderValue: string
  dropDownListFilters: any[]
  dropDownWindowFilters: any[]
  preFill: any
  generalFilterField: any
}

export interface SwipeAction {
  title: string
  command: GETCommandType
}

export interface ContextMenuAction {
  id?: number
  internalId: string
  title: string
  icon?: VectorIconType | null
  actionCode?: MOBILE_ACTION_CODE
  actionMeta?: {
    // Для NAVIGATE action
    isModal?: boolean
    formConfigId?: string
    modalTitle?: string
    onSubmitVariable?: string
    screenId?: string
    screenName?: string
    navigationParams?: string
    // Для EXECUTE_COMMAND action
    command?: GETCommandType
    // Для EXECUTE_JS action
    jsCode?: string
    // Для CHANGE_VARIABLE_VALUE action
    variableName?: string
    variableValue?: string
    [key: string]: any
  }
}

export interface CopyDialogData {
  id: number
  title?: string
}

// Advanced Template Types
export interface ItemTemplate {
  children: TemplateElement[]
}

export type TemplateElement = ViewElement | IconElement | FieldElement

export interface ViewElement {
  type: 'view'
  style?: ElementStyle
  children?: TemplateElement[]
  displayRule?: DisplayRule
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
}

export interface IconElement {
  type: 'icon'
  isJS?: boolean
  size?: number
  value: string
  displayRule?: DisplayRule
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
  /** Icon color (e.g. "#FF9800"). Has priority over displayRule. */
  color?: string
}

export interface FieldElement {
  type: 'field'
  id?: number
  internalId?: string | null
  code: string
  order?: number
  title?: string
  value: string
  pinnedColumn?: string | null
  objectValue?: string | null
  pathArray?: PathArrayItem[] | null
  actions?: any[]
  columnToSort?: string | null
  objectCode: string
  valueType?: string | null
  fieldType?: string | null
  bindType?: 'field' | 'js'
  formatDate?: string | null
  editable?: boolean
  enumDescription?: boolean
  visibility?: boolean
  useLocalParameters?: boolean
  useDisplayRule?: boolean
  isPk?: boolean | null
  parameters?: FieldParameter[]
  restrictions?: any
  valueFormat?: any
  displayRule?: DisplayRule | null
  preFill?: any
  hideWhenEmpty?: boolean
  useStyleParameters?: boolean
  styleParameters?: StyleParameters
  version?: any
  changed?: any
  createdOn?: string | null
  updatedOn?: string | null
}

export interface ElementStyle {
  flexDirection?: 'row' | 'column'
  flex?: number
  flexWrap?: 'wrap' | 'no-wrap'
  gap?: number
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around'
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'stretch'
  textAlign?: 'center' | 'left' | 'right'
  [key: string]: any
}

export interface StyleParameters {
  color?: string
  backgroundColor?: string
  borderWidth?: number | string
  borderColor?: string
  borderRadius?: number | string
  padding?: number | string
  margin?: number | string
  fontSize?: number | string
  [key: string]: any
}

// Mobile Actions Types
export enum MOBILE_EVENT_CODE {
  ON_INIT = 'on_init',
  ON_UNMOUNT = 'on_unmount',
  ON_LIST_ITEM_PRESS = 'on_list_item_press',
  ON_BUTTON_PRESS = 'on_button_press',
  ON_BUTTON_SWIPE = 'on_button_swipe',
  ON_FIELD_VALUE_CHANGE = 'on_field_value_change',
  ON_VARIABLE_CHANGE = 'on_variable_change',
  ON_INTERVAL = 'on_interval',
  ON_CHECKBOX_EMBEDDED_LIST_CHANGE = 'on_checkbox_embedded_list_change',
  ON_EMBEDDED_LIST_ITEM_CONTEXT_MENU = 'on_embedded_list_item_context_menu',
  ON_DRAWER_ITEM_PRESS = 'on_drawer_item_press',
  ON_FAB_PRESS = 'on_fab_press',
  ON_SHEET_OPENED = 'on_sheet_opened',
  ON_SHEET_CLOSED = 'on_sheet_closed',
  ON_CONDITION = 'on_condition',
}

export enum MOBILE_ACTION_CODE {
  NAVIGATE = 'navigate',
  EXECUTE_COMMAND = 'execute_command',
  EXECUTE_JS = 'execute_js',
  CHANGE_VARIABLE_VALUE = 'change_variable_value',
  GET_CURRENT_POSITION = 'get_current_position',
  START_INTERVAL = 'start_interval',
  STOP_INTERVAL = 'stop_interval',
  OPEN_SHEET = 'open_sheet',
  CLOSE_SHEET = 'close_sheet',
  CONDITION = 'condition',
  SHOW_TOAST = 'show_toast',
  LOGOUT = 'logout',
}

export interface MobileAction {
  internalId: string
  actionCode: MOBILE_ACTION_CODE
  dependsOn?: string[]
  meta?: {
    screenId?: string
    screenName?: string
    navigationParams?: string
    command?: GETCommandType
    // For EXECUTE_JS action
    jsCode?: string
    jsResultType?: string
    jsResultFields?: Array<{
      name: string
      type: string
      description?: string
    }>
    // For modal forms (NAVIGATE action with isModal)
    isModal?: boolean
    formConfigId?: string
    modalTitle?: string
    onSubmitVariable?: string
    [key: string]: any
  }
}

export interface MobileEventFlow {
  internalId: string
  eventCode: MOBILE_EVENT_CODE
  actions: MobileAction[]
  elementInternalId?: string
  fieldInternalId?: string
  intervalId?: string
  /** For ON_DRAWER_ITEM_PRESS: internalId of the drawer item (preferred) */
  drawerItemInternalId?: string
  /** LEGACY: For ON_DRAWER_ITEM_PRESS: index of the drawer item (0-based). Prefer drawerItemInternalId. */
  drawerItemIndex?: number
  /** For ON_CHECKBOX_EMBEDDED_LIST_CHANGE: ID of the EMBEDDED_LIST form element */
  elementId?: string
  /** For ON_VARIABLE_CHANGE: filter by variable (optional - leave empty for all variables) */
  variableName?: string
  /** For ON_VARIABLE_CHANGE: scope of the variable (background | sheetForm | global) */
  variableScope?: 'local' | 'global' | 'background' | 'sheetForm'
  /** For ON_CONDITION: branch identifier used by CONDITION action to select this flow */
  conditionBranchId?: string
  /** Open modal before triggering this event (confirmation or comment input) */
  openModalBeforeTrigger?: boolean
  /** Type of modal: confirmation (Yes/No), comment (text input), or picker (select from variable) */
  modalType?: 'confirmation' | 'comment' | 'picker'
  /** Modal window title */
  modalTitle?: string
  /** Modal window body text */
  modalText?: string
  /** For modalType === 'comment': variable internalId to store the comment (e.g. form variable) */
  modalCommentVariable?: string
  /** For modalType === 'picker': variable internalId (multiple type) to get options from */
  modalSourceVariable?: string
  /** For modalType === 'picker': field path to display in picker (e.g. "label", "name") */
  modalDisplayedValue?: string
  /** For modalType === 'picker': variable internalId to write selected value to */
  modalTargetVariable?: string
  /** For modalType === 'picker': pre-select first option when current value is empty */
  modalDefaultFirstOption?: boolean
  /** Optional JS condition: show modal only when this expression returns true. If empty, modal always shows when openModalBeforeTrigger is true. */
  showModalIfJs?: string
}

export interface MobileSearchAssistant {
  configMode: string
  id: string
  title: string
  objectCode?: string
  filters: SearchFilter[]
  variables?: Variable[]
  eventFlows?: MobileEventFlow[]
}

export interface SearchFilter {
  id: string
  order: number
  type: SearchFilterType
  label: string
  fieldCode: string
  operator: RsqlOperator
  config?: SearchFilterConfig
}

export type SearchFilterType =
  | 'SEARCHBAR' // Текстовый поиск
  | 'SELECT' // Выбор из списка (одно значение)
  | 'MULTISELECT' // Выбор из списка (несколько значений)
  | 'TOGGLE_BUTTONS' // Кнопки-тогглы (как тип рейса)
  | 'DAY_OFFSET' // Смещение по дням
  | 'DATE_RANGE' // Диапазон дат
  | 'CHECKBOX' // Чекбокс

export type RsqlOperator =
  | '==' // equals
  | '!=' // not equals
  | '=in=' // in list
  | '=out=' // not in list
  | '=ge=' // greater or equal
  | '=le=' // less or equal
  | '=gt=' // greater than
  | '=lt=' // less than
  | '=isnull=' // is null
  | '=isnotnull=' // is not null
  | '==*' // like (contains)

export interface SearchFilterConfig {
  // Для SELECT/MULTISELECT/TOGGLE_BUTTONS
  options?: FilterOption[]
  // Для DAY_OFFSET
  dayOffsets?: number[] // [-2, -1, 0, 1, 2]
  // Для SEARCHBAR
  placeholder?: string
  // Для всех типов
  defaultValue?: any
  // Иконки для TOGGLE_BUTTONS
  icons?: { [value: string]: string }
  // Дополнительные данные для восстановления при редактировании
  objectCode?: string // Код объекта, к которому относится фильтр
  valueName?: string // Имя выбранного поля
  valueId?: string | number // ID выбранного поля
  valueType?: string // Тип значения
  valuePathArray?: any[] // Сохраненный путь для Value (name) - массив
  pathArray?: any[] // Сохраненный путь для ObjectValue (objectValue) - массив
  valueNameString?: string // Сохраненный путь Value как строка (для value.name)
  objectValueString?: string // Сохраненный путь ObjectValue как строка (для value.objectValue)
  asCheckbox?: boolean // Отображать как чекбокс
}

export interface FilterOption {
  label: string
  value: string | number
  icon?: string // Для TOGGLE_BUTTONS
}

export interface MobileConfigStorage {
  mobileApps: MobileApp[]
  mobileScreens: MobileScreen[]
  mobileListItems: MobileListItem[]
  mobileForms: MobileForm[]
  mobileSearchAssistants: MobileSearchAssistant[]
  mobileGlobalVariables: Variable[]
}

export interface ListItemPreviewSettings {
  skipFilterGetData: boolean
  selectedLocalVariablePackageId?: string
  selectedGlobalVariablePackageId?: string
}

export const DEFAULT_LIST_ITEM_PREVIEW_SETTINGS: ListItemPreviewSettings = {
  skipFilterGetData: true,
}

export interface PreviewVariablePackage {
  id: string
  name: string
  values: Record<string, any>
}

export interface PreviewVariableValues {
  localVariables: Record<string, any>
  globalVariables: Record<string, any>
}
